/**
 * Print Overview chapter (exported as PdfChapterArchetype).
 * Canva page-02 editorial layout: meta, hero, theme, glance scores, season/palace.
 * ContractCard stays on Letter (Phase 4).
 */

import React from "react";
import { BarChart3, Compass, Mountain, Shield, Target, TrendingUp, Users } from "lucide-react";
import type { MonthlyConsultationBundle } from "../../../../utils/forecast/monthlyConsultation";
import {
  liuSeasonPlain,
  liuSeasonShort,
} from "../../../../utils/forecast/monthlyConsultation/englishLabels";
import heroOverview from "../../../../assets/monthly-consultation/pdf/hero-overview.jpg";
import {
  PdfChapterShell,
  PdfPill,
  PdfScoreBar,
} from "../atoms";
import {
  buildReportId,
  clipPdfText,
  EXECUTIVE_REMINDER_POINTS,
  EXECUTIVE_REMINDER_QUOTE,
  monthPosterLabel,
  palaceFocusBody,
  palaceFocusTags,
  scoreQualityLabel,
  seasonFocusBody,
  seasonFocusChips,
  stripPalaceSuffix,
} from "../pdfCopy";
import {
  bodyFontFamily,
  border,
  cardShadow,
  cardWhite,
  charcoal,
  deepRed,
  displayFontFamily,
  mutedGray,
  tanGold,
  terracotta,
} from "../pdfTheme";

/**
 * Props for the PDF overview (archetype) chapter.
 */
export interface PdfChapterArchetypeProps {
  /** Full monthly consultation payload. */
  bundle: MonthlyConsultationBundle;
  /** 1-based sequential page index among rendered sheets. */
  pageNumber?: number;
}

const REMINDER_ICONS = [Target, Compass, TrendingUp] as const;

/**
 * Resolve the webpack URL for the overview mountain hero.
 *
 * @returns Image URL, or empty string when the module did not yield a path
 */
function overviewHeroSrc(): string {
  return typeof heroOverview === "string" ? heroOverview.trim() : "";
}

/**
 * Three Overview chips under season or palace coaching.
 * Title-case chips (Canva), not PdfPill uppercase.
 *
 * @param labels - Chip labels (season or palace tags)
 * @param color - Text color token
 * @param bg - Fill color token
 */
const OverviewChipRow: React.FC<{
  labels: readonly string[];
  color: string;
  bg: string;
}> = ({ labels, color, bg }) => {
  if (!Array.isArray(labels) || labels.length === 0) {
    return null;
  }
  return (
    <div
      style={{
        display: "flex",
        flexWrap: "nowrap",
        gap: 8,
        marginTop: 12,
        overflow: "visible",
      }}
    >
      {labels.map((label) => (
        <span
          key={label}
          style={{
            display: "inline-block",
            padding: "4px 11px",
            borderRadius: 999,
            fontFamily: bodyFontFamily,
            fontSize: 10,
            fontWeight: 700,
            letterSpacing: "0.04em",
            lineHeight: 1.4,
            color,
            backgroundColor: bg,
            whiteSpace: "nowrap",
            flexShrink: 0,
          }}
        >
          {label}
        </span>
      ))}
    </div>
  );
};

/**
 * Map a scorecard dimension label to a Canva accent token.
 *
 * @param label - Scorecard row label
 * @returns Theme color for the bar fill
 */
function scoreBarColorForLabel(label: string): string {
  const key = typeof label === "string" ? label.trim().toLowerCase() : "";
  if (key.includes("career") || key.includes("work")) {
    return deepRed;
  }
  if (key.includes("wealth") || key.includes("money")) {
    return tanGold;
  }
  if (key.includes("relation")) {
    return terracotta;
  }
  if (key.includes("health") || key.includes("body")) {
    return charcoal;
  }
  return terracotta;
}

/**
 * Month Overview — Canva editorial sheet (one A4).
 */
export const PdfChapterArchetype: React.FC<PdfChapterArchetypeProps> = ({
  bundle,
  pageNumber,
}) => {
  const profileName =
    typeof bundle.profileName === "string" ? bundle.profileName.trim() : "";
  const monthLine = monthPosterLabel(
    typeof bundle.monthLabel === "string" ? bundle.monthLabel : "",
    bundle.lunarMonth,
    bundle.solarYear
  );
  const archetypeName =
    typeof bundle.archetype.name === "string" ? bundle.archetype.name.trim() : "";
  const description =
    typeof bundle.archetype.description === "string"
      ? bundle.archetype.description.trim()
      : "";
  const identityLine =
    typeof bundle.archetype.identityLine === "string"
      ? bundle.archetype.identityLine.trim()
      : "";
  const priority =
    typeof bundle.briefing.priority === "string"
      ? bundle.briefing.priority.trim()
      : "";
  const area =
    typeof bundle.briefing.area === "string" ? bundle.briefing.area.trim() : "";
  const seasonShort = liuSeasonShort(bundle.briefing.season);
  const seasonTitle =
    seasonShort.length > 0 ? `${seasonShort} Season` : "Current season";
  const seasonBody = seasonFocusBody(
    liuSeasonPlain(bundle.briefing.season),
    seasonShort
  );
  const seasonChips = seasonFocusChips(seasonShort);

  const chartPalace =
    typeof bundle.stack.liuYueLifeChartPalaceNameEnglish === "string"
      ? bundle.stack.liuYueLifeChartPalaceNameEnglish.trim()
      : "";
  const natalPalace =
    typeof bundle.stack.liuYueLifePalaceNameEnglish === "string"
      ? bundle.stack.liuYueLifePalaceNameEnglish.trim()
      : "";
  const natalShort = stripPalaceSuffix(natalPalace);
  const palaceTitle =
    chartPalace.length > 0 &&
    natalShort.length > 0 &&
    stripPalaceSuffix(chartPalace).toLowerCase() !== natalShort.toLowerCase()
      ? `${chartPalace} (${natalShort})`
      : chartPalace.length > 0
        ? chartPalace
        : natalPalace.length > 0
          ? natalPalace
          : "Life Palace";

  const keyActions = Array.isArray(bundle.briefing.guidance.keyActions)
    ? bundle.briefing.guidance.keyActions
    : [];
  const firstActionRaw = keyActions.find(
    (item) => typeof item === "string" && item.trim().length > 0
  );
  const firstAction =
    typeof firstActionRaw === "string" ? firstActionRaw.trim() : "";
  const palaceBody = palaceFocusBody(natalPalace, chartPalace, area, firstAction);
  const palaceChips = palaceFocusTags(natalPalace, chartPalace, area);

  const daXian = bundle.stack.daXian;
  const decadeLabel =
    daXian !== null
      ? `DECADE Y${String(daXian.yearInCycle)}/10`
      : "DECADE —";
  const cycleLabel =
    natalShort.length > 0 ? `${natalShort} Cycle` : seasonTitle;
  const reportId = buildReportId(
    profileName,
    bundle.solarYear,
    bundle.lunarMonth
  );

  const preparedBits: string[] = [];
  if (profileName.length > 0) {
    preparedBits.push(`Prepared for: ${profileName}`);
  }
  if (chartPalace.length > 0 || seasonTitle.length > 0) {
    const palaceBit = chartPalace.length > 0 ? chartPalace : "Life Palace";
    preparedBits.push(`${palaceBit} (${seasonTitle})`);
  }
  const preparedLine = preparedBits.join(" • ");

  const scoreRows = Array.isArray(bundle.scorecard) ? bundle.scorecard : [];
  const hero = overviewHeroSrc();

  return (
    <PdfChapterShell
      chapterId="archetype"
      eyebrow=""
      title=""
      pageNumber={pageNumber}
      hideHeader={true}
      compact
    >
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 12,
          flex: "1 1 auto",
          minHeight: 0,
          fontFamily: bodyFontFamily,
        }}
      >
        {/* Editorial header: brand pill, month title, decade meta */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            gap: 16,
          }}
        >
          <div style={{ minWidth: 0, flex: 1 }}>
            <span
              style={{
                display: "inline-block",
                padding: "4px 12px",
                borderRadius: 999,
                backgroundColor: charcoal,
                color: cardWhite,
                fontSize: 8,
                fontWeight: 700,
                letterSpacing: "0.14em",
                textTransform: "uppercase",
                lineHeight: 1.4,
              }}
            >
              ZI WEI DOU SHU • MONTHLY REPORT
            </span>
            <h2
              style={{
                margin: "8px 0 4px",
                fontFamily: displayFontFamily,
                fontSize: 28,
                fontWeight: 700,
                letterSpacing: "-0.02em",
                color: charcoal,
                lineHeight: 1.1,
              }}
            >
              {monthLine}
            </h2>
            {preparedLine.length > 0 ? (
              <p
                style={{
                  margin: 0,
                  fontFamily: displayFontFamily,
                  fontSize: 12,
                  color: charcoal,
                  lineHeight: 1.4,
                }}
              >
                {preparedLine}
              </p>
            ) : null}
          </div>
          <div
            style={{
              textAlign: "right",
              flexShrink: 0,
              fontFamily: bodyFontFamily,
            }}
          >
            <p
              style={{
                margin: 0,
                fontSize: 10,
                fontWeight: 700,
                letterSpacing: "0.08em",
                textTransform: "uppercase",
                color: terracotta,
              }}
            >
              {decadeLabel}
            </p>
            <p
              style={{
                margin: "4px 0 0",
                fontSize: 11,
                fontWeight: 600,
                color: charcoal,
              }}
            >
              {cycleLabel}
            </p>
            <p
              style={{
                margin: "4px 0 0",
                fontSize: 9,
                letterSpacing: "0.06em",
                textTransform: "uppercase",
                color: mutedGray,
              }}
            >
              {`ID: ${reportId}`}
            </p>
          </div>
        </div>

        {hero.length > 0 ? (
          <img
            src={hero}
            alt=""
            className="mc-pdf-hero-img mc-pdf-hero-img--overview"
          />
        ) : null}

        {/* Month theme + identity quote */}
        <div
          className="mc-pdf-card"
          style={{
            background: cardWhite,
            border: `1px solid ${border}`,
            borderRadius: 14,
            boxShadow: cardShadow,
            padding: "12px 14px",
            boxSizing: "border-box",
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 8,
              marginBottom: 8,
            }}
          >
            <PdfPill label="Month theme" color={tanGold} bg={`${tanGold}22`} />
            {priority.length > 0 ? (
              <PdfPill
                label={`Priority: ${priority}`}
                color={terracotta}
                bg={`${terracotta}18`}
              />
            ) : null}
          </div>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: identityLine.length > 0 ? "1.6fr 1fr" : "1fr",
              gap: 12,
              alignItems: "stretch",
            }}
          >
            <div>
              <h3
                style={{
                  margin: "0 0 6px",
                  fontFamily: displayFontFamily,
                  fontSize: 16,
                  fontWeight: 700,
                  color: charcoal,
                  lineHeight: 1.25,
                }}
              >
                {archetypeName.length > 0 ? archetypeName : "Month theme"}
              </h3>
              {description.length > 0 ? (
                <p
                  style={{
                    margin: 0,
                    fontSize: 12,
                    lineHeight: 1.45,
                    color: mutedGray,
                  }}
                >
                  {clipPdfText(description, 220)}
                </p>
              ) : null}
            </div>
            {identityLine.length > 0 ? (
              <div
                style={{
                  borderLeft: `1px solid ${border}`,
                  paddingLeft: 12,
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <p
                  style={{
                    margin: 0,
                    fontFamily: displayFontFamily,
                    fontSize: 13,
                    fontStyle: "italic",
                    lineHeight: 1.45,
                    color: charcoal,
                  }}
                >
                  {`“${identityLine}”`}
                </p>
              </div>
            ) : null}
          </div>
        </div>

        {/* At a glance scorecard */}
        <div>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 8,
              marginBottom: 8,
            }}
          >
            <p
              style={{
                margin: 0,
                display: "flex",
                alignItems: "center",
                gap: 6,
                fontSize: 10,
                fontWeight: 700,
                letterSpacing: "0.12em",
                textTransform: "uppercase",
                color: charcoal,
              }}
            >
              <BarChart3 size={13} color={tanGold} strokeWidth={2} aria-hidden="true" />
              At a glance • Scorecard
            </p>
            <p
              style={{
                margin: 0,
                fontSize: 9,
                letterSpacing: "0.04em",
                color: mutedGray,
              }}
            >
              {`${monthLine} Forecast Metrics`}
            </p>
          </div>
          {scoreRows.length > 0 ? (
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {scoreRows.map((row) => {
                const label =
                  typeof row.label === "string" ? row.label.trim() : "";
                const pct = typeof row.pct === "number" ? row.pct : 0;
                return (
                  <PdfScoreBar
                    key={label.length > 0 ? label : `score-${String(pct)}`}
                    label={label.length > 0 ? label : "Area"}
                    pct={pct}
                    color={scoreBarColorForLabel(label)}
                    qualityLabel={scoreQualityLabel(pct)}
                  />
                );
              })}
            </div>
          ) : (
            <p style={{ margin: 0, fontSize: 12, color: mutedGray }}>
              Scorecard metrics are not available for this month.
            </p>
          )}
        </div>

        {/* Current season vs core palace focus — grow into leftover A4 space */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: 12,
            flex: "1 1 auto",
            alignItems: "stretch",
            minHeight: 140,
          }}
        >
          <div
            className="mc-pdf-card"
            style={{
              background: cardWhite,
              border: `1px solid ${border}`,
              borderRadius: 16,
              padding: "18px 18px",
              boxSizing: "border-box",
              display: "flex",
              flexDirection: "column",
              minHeight: 0,
            }}
          >
            <p
              style={{
                margin: "0 0 12px",
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: "0.14em",
                textTransform: "uppercase",
                color: tanGold,
              }}
            >
              Current season
            </p>
            <div style={{ display: "flex", gap: 12, alignItems: "flex-start", flex: 1 }}>
              <div
                aria-hidden="true"
                style={{
                  width: 40,
                  height: 40,
                  borderRadius: "50%",
                  background: `${tanGold}22`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                <Shield size={20} color={tanGold} strokeWidth={2} />
              </div>
              <div style={{ minWidth: 0, flex: 1 }}>
                <p
                  style={{
                    margin: 0,
                    fontFamily: displayFontFamily,
                    fontSize: 18,
                    fontWeight: 700,
                    color: charcoal,
                    lineHeight: 1.2,
                  }}
                >
                  {seasonTitle}
                </p>
                {seasonBody.length > 0 ? (
                  <p
                    style={{
                      margin: "8px 0 0",
                      fontSize: 13,
                      lineHeight: 1.5,
                      color: mutedGray,
                    }}
                  >
                    {clipPdfText(seasonBody, 260)}
                  </p>
                ) : null}
                <OverviewChipRow
                  labels={seasonChips}
                  color={charcoal}
                  bg={`${mutedGray}18`}
                />
              </div>
            </div>
          </div>

          <div
            className="mc-pdf-card"
            style={{
              background: cardWhite,
              border: `1px solid ${border}`,
              borderRadius: 16,
              padding: "18px 18px",
              boxSizing: "border-box",
              display: "flex",
              flexDirection: "column",
              minHeight: 0,
            }}
          >
            <p
              style={{
                margin: "0 0 12px",
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: "0.14em",
                textTransform: "uppercase",
                color: terracotta,
              }}
            >
              Core palace focus
            </p>
            <div style={{ display: "flex", gap: 12, alignItems: "flex-start", flex: 1 }}>
              <div
                aria-hidden="true"
                style={{
                  width: 40,
                  height: 40,
                  borderRadius: "50%",
                  background: `${terracotta}18`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                <Users size={20} color={terracotta} strokeWidth={2} />
              </div>
              <div style={{ minWidth: 0, flex: 1 }}>
                <p
                  style={{
                    margin: 0,
                    fontFamily: displayFontFamily,
                    fontSize: 18,
                    fontWeight: 700,
                    color: charcoal,
                    lineHeight: 1.2,
                  }}
                >
                  {palaceTitle}
                </p>
                {palaceBody.length > 0 ? (
                  <p
                    style={{
                      margin: "8px 0 0",
                      fontSize: 13,
                      lineHeight: 1.5,
                      color: mutedGray,
                    }}
                  >
                    {clipPdfText(palaceBody, 240)}
                  </p>
                ) : null}
                <OverviewChipRow
                  labels={palaceChips}
                  color={terracotta}
                  bg={`${terracotta}14`}
                />
              </div>
            </div>
          </div>
        </div>

        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 16,
            width: "100%",
            boxSizing: "border-box",
            padding: "16px 18px",
            borderRadius: 14,
            background: `linear-gradient(90deg, ${tanGold}26, ${tanGold}12)`,
            border: `1px solid ${tanGold}33`,
            flexShrink: 0,
          }}
        >
          <div
            aria-hidden="true"
            style={{
              width: 40,
              height: 40,
              borderRadius: "50%",
              background: `${tanGold}28`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <Mountain size={20} color={tanGold} strokeWidth={2} />
          </div>
          <div style={{ minWidth: 0, flex: 1 }}>
            <p
              style={{
                margin: 0,
                fontSize: 10,
                fontWeight: 700,
                letterSpacing: "0.14em",
                textTransform: "uppercase",
                color: tanGold,
              }}
            >
              Executive reminder
            </p>
            <p
              style={{
                margin: "6px 0 0",
                fontFamily: displayFontFamily,
                fontSize: 15,
                fontStyle: "italic",
                lineHeight: 1.4,
                color: charcoal,
              }}
            >
              {`“${EXECUTIVE_REMINDER_QUOTE}”`}
            </p>
          </div>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr 1fr",
              gap: 10,
              flex: "1 1 auto",
              minWidth: 0,
              alignItems: "center",
            }}
          >
            {EXECUTIVE_REMINDER_POINTS.map((point, index) => {
              const resolvedIcon = REMINDER_ICONS[index];
              const Icon = resolvedIcon !== undefined ? resolvedIcon : Target;
              return (
                <div
                  key={point}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 8,
                    minWidth: 0,
                  }}
                >
                  <div
                    aria-hidden="true"
                    style={{
                      width: 28,
                      height: 28,
                      borderRadius: "50%",
                      background: `${terracotta}18`,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    <Icon size={14} color={terracotta} strokeWidth={2} />
                  </div>
                  <p
                    style={{
                      margin: 0,
                      fontSize: 11,
                      fontWeight: 700,
                      lineHeight: 1.3,
                      color: charcoal,
                      minWidth: 0,
                    }}
                  >
                    {point}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </PdfChapterShell>
  );
};

export default PdfChapterArchetype;
